import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { UserAuth } from '@/authentication/AuthContext.jsx';
import { getScopeLifecycleByGameId } from '@/services/admin-settings/GameService.jsx';
import { gameActionPolicy } from '@/utils/gameLifecycle.js';

// Short-TTL, in-flight-deduped cache. The hook is used in several places per page (global banner,
// page header, each data table), all keyed on the same selected scope — without this they'd each
// fire a separate lookup. TTL keeps it fresh enough to reflect a suspend/activate within seconds.
const TTL_MS = 15_000;
const cache = new Map(); // cacheKey -> { ts, snapshot }
const inflight = new Map(); // cacheKey -> Promise<snapshot>

// A snapshot mirrors what the backend scope-lifecycle guard walks: the GAME's state. A non-ACTIVE
// game freezes content writes — the server enforces it by 409ing the chain org → game.
//
// `clientState` is carried through but is always null: there is no lifecycle below the game any
// more (GameRegistryApi.lifecycleMap hard-codes it), and the key survives only so the two are
// dropped together, here and there. (Org-level state isn't carried at all — org-suspend stays a
// fail-open gap the server still enforces.)
function fetchSnapshot({ gameId, accessToken }) {
    const cacheKey = `g:${gameId}`;
    const hit = cache.get(cacheKey);
    if (hit && Date.now() - hit.ts < TTL_MS) {
        return Promise.resolve(hit.snapshot);
    }
    if (inflight.has(cacheKey)) {
        return inflight.get(cacheKey);
    }

    // Minimal, auth-only lifecycle endpoint — reachable by every game member (incl. Developers,
    // who can't read the full game-registry endpoint and were getting 403 → fail-open). It returns
    // { gameState, clientState } directly, so no response-shape/builds digging here.
    const p = getScopeLifecycleByGameId({ gameId, accessToken })
        .then((data) => {
            const snapshot = {
                gameState: data?.gameState ?? null,
                clientState: data?.clientState ?? null,
            };
            cache.set(cacheKey, { ts: Date.now(), snapshot });
            return snapshot;
        })
        .catch(() => ({ gameState: null, clientState: null })) // fail-open: don't freeze on lookup failure
        .finally(() => inflight.delete(cacheKey));
    inflight.set(cacheKey, p);
    return p;
}

const EMPTY = { gameState: null, clientState: null };

// Event other code dispatches after a lifecycle transition (suspend / activate / sunset) so mounted
// hooks drop their cached snapshot and re-resolve immediately instead of waiting out the TTL.
export const LIFECYCLE_CHANGED_EVENT = 'gp-lifecycle-changed';

/** Clears the lifecycle snapshot cache and notifies mounted hooks to re-resolve. Call after a
 *  suspend / activate / sunset succeeds. */
export function notifyLifecycleChanged() {
    cache.clear();
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(LIFECYCLE_CHANGED_EVENT));
    }
}

/**
 * Resolves the currently-selected scope's lifecycle and the UI freeze policy that mirrors the backend
 * scope-lifecycle guard. A suspended/sunset GAME or the suspended/sunset selected CLIENT freezes
 * content edits + live-ops / AI Studio writes (the server enforces it with 409 GP-SCOPE-NOT-ACTIVE).
 * Resolves from a selected client when present (game + client state), otherwise from the selected game
 * (game state). Use this to disable create/edit affordances and surface a banner up front instead of
 * letting the user hit the error.
 *
 * Fails OPEN: while loading or on error/unknown, {@code frozen} is false so actions are never
 * wrongly disabled.
 *
 * @returns {{ state: (string|null), gameState: (string|null), clientState: (string|null),
 *             frozen: boolean, frozenReason: (string|null), loading: boolean }}
 */
export default function useSelectedGameLifecycle() {
    const { user } = UserAuth();
    const selectedGameId = useSelector((s) => s.dashboardContext.selectedGameId) || '';
    // While the new-game wizard is open, the freshly-created game is in scope only so its
    // profile/icon calls can be header-scoped — its lifecycle (suspend/sunset) is meaningless
    // and the lookup is pure noise, so skip it entirely during onboarding.
    const onboardingActive = useSelector((s) => !!s.dashboardContext.onboardingDraft?.active);
    const [snapshot, setSnapshot] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    // Bumped by a lifecycle-changed event to force an immediate re-resolve (cache already cleared).
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        const onChanged = () => {
            // Drop cached snapshots so the forced re-resolve hits the server, not the TTL cache.
            // The event may originate from another module (e.g. the axios 409 backstop) that can't
            // clear this module's private cache itself.
            cache.clear();
            setRefreshTick((t) => t + 1);
        };
        window.addEventListener(LIFECYCLE_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(LIFECYCLE_CHANGED_EVENT, onChanged);
    }, []);

    useEffect(() => {
        let cancelled = false;
        // Nothing to resolve unless some scope is selected — and never during onboarding.
        if (!selectedGameId || !user?.accessToken || onboardingActive) {
            setSnapshot(EMPTY);
            return undefined;
        }
        setLoading(true);
        fetchSnapshot({ gameId: selectedGameId, accessToken: user.accessToken })
            .then((resolved) => { if (!cancelled) setSnapshot(resolved); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [selectedGameId, user?.accessToken, refreshTick, onboardingActive]);

    const { gameState, clientState } = snapshot;

    // Freeze if EITHER the game or the selected client is non-ACTIVE. Prefer the game-level reason
    // (broader blast radius); otherwise surface a client-specific reason.
    const gamePolicy = gameActionPolicy(gameState);
    const clientPolicy = gameActionPolicy(clientState);
    let frozenReason = gamePolicy.frozenReason;
    if (!frozenReason && clientPolicy.frozenReason) {
        frozenReason = clientState === 'SUNSET'
            ? 'This game client is sunset — it is read-only.'
            : 'This game client is suspended — reactivate it to make changes.';
    }

    return {
        // `state` kept for back-compat: the effective game state (callers that only show game status).
        state: gameState,
        gameState,
        clientState,
        frozen: !!frozenReason,
        frozenReason,
        loading,
    };
}
